import { useCallback, useEffect, useState } from 'react';

/** What is on top of the current tab, if anything. */
export type Overlay = 'now' | 'focus' | null;

export type NavState = { tab: string; overlay: Overlay };

type Entry = { aj: NavState };

const isEntry = (value: unknown): value is Entry =>
  typeof value === 'object' &&
  value !== null &&
  'aj' in value &&
  typeof (value as Entry).aj?.tab === 'string';

/**
 * Navigation, with the phone's Back button actually working.
 *
 * The tab used to be a plain `useState`, which on an installed Android PWA
 * means Back **exits the app** — from anywhere, including out of a running
 * focus session and out of the Now sheet. For an app on someone's home
 * screen that does not read as minimal, it reads as broken, and it is the
 * kind of broken that loses a session's work.
 *
 * So every navigation pushes a real history entry and `popstate` is the only
 * thing that moves the app backwards. Closing a sheet calls `history.back()`
 * rather than setting state directly — otherwise the entry stays on the
 * stack and Back re-opens something the student already dismissed.
 *
 * Deliberately not a router. There are no URLs to design here: an artifact
 * of being installed to a home screen is that nobody ever sees or types the
 * address, so paths would be ceremony. What is needed is a back stack, and
 * that is all this is.
 */
export function useNavigation(initialTab: string) {
  const [state, setState] = useState<NavState>({ tab: initialTab, overlay: null });

  useEffect(() => {
    // Stamp the entry we are already standing on, so the first Back has
    // somewhere recognisable to land instead of a null state.
    window.history.replaceState({ aj: { tab: initialTab, overlay: null } } satisfies Entry, '');

    const onPop = (event: PopStateEvent) => {
      // An unstamped entry means something outside this app pushed it. Going
      // to the root tab is the safe reading.
      setState(isEntry(event.state) ? event.state.aj : { tab: initialTab, overlay: null });
    };

    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [initialTab]);

  const push = useCallback((next: NavState) => {
    window.history.pushState({ aj: next } satisfies Entry, '');
    setState(next);
  }, []);

  const goTab = useCallback(
    (tab: string) => {
      setState((current) => {
        // Tapping the tab you are already on is not a navigation. Without
        // this, drumming on a tab builds a stack of identical entries and
        // Back appears to do nothing several times in a row.
        if (current.tab === tab && current.overlay === null) return current;
        window.history.pushState({ aj: { tab, overlay: null } } satisfies Entry, '');
        return { tab, overlay: null };
      });
    },
    [],
  );

  const open = useCallback(
    (overlay: Exclude<Overlay, null>) => setState((current) => {
      window.history.pushState({ aj: { ...current, overlay } } satisfies Entry, '');
      return { ...current, overlay };
    }),
    [],
  );

  /** Always through history, so the stack and the screen never disagree. */
  const close = useCallback(() => window.history.back(), []);

  /**
   * Leave the sheet and enter the session as ONE step back.
   *
   * Replacing rather than pushing means Back from a running focus session
   * returns to the tab, not to the sheet the student just chose from — going
   * back into a menu you have already acted on is a dead end that has to be
   * dismissed a second time.
   */
  const replace = useCallback(
    (overlay: Overlay) =>
      setState((current) => {
        const next = { ...current, overlay };
        window.history.replaceState({ aj: next } satisfies Entry, '');
        return next;
      }),
    [],
  );

  return { ...state, goTab, open, close, replace, push };
}
